import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import Divider from '@material-ui/core/Divider';

import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';

import CommuneService from '../../../services/commune-service';
import RegionService from '../../../services/region-service';
import AddressService from '../../../services/address-service';
import { useLoadingBar } from '../../../libraries/contexts';

import {
  getDNIField,
  getCedulaSerialNumber,
  getRegionField,
  getCommuneField,
  getAddressNumberField,
  getAddressField,
  getEmailField,
  getPhoneField,
  getAttachedField
} from './fields';

import { getFingerPrintField } from '../../../components/fields/finger';

import { ENDORSEMENT_FORM_TITLE, ADDRESS_NO_NORMALIZED } from '../../../libraries/texts';
import { isValidMinimumDNI } from '../../../libraries/validators';

import { endorsement as styles } from '../styles';

const EndorsementForm = props => {
  const { values, classes, handleBlur, errors, touched, setFieldValue, setFieldTouched } = props;
  const [showCedula, setShowCedula] = useState(false);
  const [loading, setLoading] = useLoadingBar();

  const [regions, setRegions] = useState([]);
  const [communes, setCommunes] = useState([]);
  const prefix = 'aval';

  const getRequests = async () => {
    setLoading({ principalLoading: true });
    const regions = await RegionService.getAll();
    setRegions(regions);
    setLoading({ principalLoading: false });
  };

  const changeCommunes = async () => {
    setLoading({ principalLoading: true });
    const regionVal = values[`${prefix}Region`];
    const communes = await CommuneService.perRegion(regionVal._id);
    setCommunes(communes);
    setLoading({ principalLoading: false });
  };

  const normalizeAddress = async () => {
    if (
      !values[`${prefix}Commune`] ||
      !values[`${prefix}Address`] ||
      !values[`${prefix}AddressNumber`]
    ) {
      return;
    }
    const data = AddressService.makeNormalizationData(
      values[`${prefix}Commune`] && values[`${prefix}Commune`].code
        ? values[`${prefix}Commune`].name
        : '',
      values[`${prefix}Address`],
      values[`${prefix}AddressNumber`],
      values[`${prefix}Anexo`]
    );

    let res = {};
    try {
      res = await AddressService.normalization(data);
    } catch (e) {
      setFieldValue(`is${prefix}Normalized`, false);
      return;
    }

    const { extra, number, commune, address, isNormalized = true } = res;
    setFieldValue(`is${prefix}Normalized`, isNormalized);

    if (!!address && values[`${prefix}Address`] !== address) {
      setFieldValue(`${prefix}Address`, address);
    }
    if (
      commune &&
      commune.code &&
      values[`${prefix}Commune`] &&
      values[`${prefix}Commune`].code &&
      commune.code !== values[`${prefix}Commune`].code
    ) {
      setFieldValue(`${prefix}Commune`, commune);
    }
    if (values[`${prefix}Anexo`] !== extra) {
      if (extra !== 'UNDEFINED') {
        setFieldValue(`${prefix}Anexo`, extra);
      } else {
        setFieldValue(`${prefix}Anexo`, '');
      }
    }
    if (!!number && values[`${prefix}AddressNumber`] !== number) {
      setFieldValue(`${prefix}AddressNumber`, number);
    }
  };

  useEffect(() => {
    [...document.getElementsByTagName('circle')].map(elem => elem.setAttribute('r', 35));
    getRequests();
  }, []);

  useEffect(() => {
    const regionVal = values[`${prefix}Region`];
    setCommunes([]);
    if (!regionVal || !regionVal.code) {
      setFieldValue(`${prefix}Commune`, '');
      return;
    }
    setFieldValue(`${prefix}Commune`, '');
    changeCommunes();
  }, [values[`${prefix}Region`]]);

  useEffect(() => {
    normalizeAddress();
  }, [values[`${prefix}normalizeAddress`]]);

  const fieldProps = {
    handleBlur,
    classes,
    touched,
    errors,
    values,
    setFieldTouched,
    setFieldValue,
    prefix
  };

  const dniValue = values[`${prefix}Dni`] === 'NaN' ? '' : values[`${prefix}Dni`];

  return (
    <Card className={classes.card}>
      <CardHeader title={ENDORSEMENT_FORM_TITLE} className={classes.cardTitle} />
      <CardContent className={classes.cardContent}>
        {values ? (
          <React.Fragment>
            <div className={classes.middleWidthContainer}>
              {getDNIField({
                disabled: values[`${prefix}DniDisabled`],
                ...fieldProps,
                name: `${prefix}Dni`,
                label: `Rut ${prefix}`,
                formControlClass: classes.fullWidth,
                setShowCedula
              })}
              {getCedulaSerialNumber({
                ...fieldProps,
                name: `${prefix}Cedula`,
                label: `Número de Serie Cédula ${prefix}`,
                showCedula,
                formControlClass: classes.fullWidth,
                isMinDigitsDNI: !isValidMinimumDNI(dniValue)
              })}
            </div>
            {getFingerPrintField({ ...fieldProps, available: showCedula })}
            <Divider />
            {getRegionField({
              ...fieldProps,
              name: `${prefix}Region`,
              label: 'Región *',
              options: regions,
              loading
            })}
            {getCommuneField({
              ...fieldProps,
              name: `${prefix}Commune`,
              label: 'Comuna *',
              options: communes,
              loading
            })}
            {getAddressField({ ...fieldProps, name: `${prefix}Address`, label: 'Dirección' })}
            {getAddressNumberField({
              ...fieldProps,
              name: `${prefix}AddressNumber`,
              label: 'Número Dirección',
              required: true
            })}
            {getAttachedField({ ...fieldProps, name: `${prefix}Anexo`, label: 'Anexo' })}
            {!values.isavalNormalized ? (
              <div className={classNames(classes.fullWidthError)}>{ADDRESS_NO_NORMALIZED}</div>
            ) : (
              ''
            )}
            <Divider />
            {getEmailField({
              ...fieldProps,
              name: `${prefix}Email`,
              label: `Email ${prefix}`
            })}
            {getPhoneField({
              ...fieldProps,
              name: `${prefix}Tel1`,
              label: 'Teléfono contacto 1',
              required: true
            })}
            {getPhoneField({
              ...fieldProps,
              name: `${prefix}Tel2`,
              label: 'Teléfono contacto 2',
              required: true
            })}
          </React.Fragment>
        ) : (
          ''
        )}
      </CardContent>
    </Card>
  );
};

EndorsementForm.propTypes = {
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(EndorsementForm);
